import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { WithWrite } from '../../lib/rbac';
import { billingApi } from '../../services/billing';
import { settingsApi, type ModuleRow, type SettingsUser } from '../../services/settings';
import { TableWrapper, TableHead, FilterRow } from '../../components/TableWrapper';

const ACCESS_LEVELS = ['NONE', 'READ', 'WRITE', 'ADMIN'] as const;

export function UserPermissionsPage() {
  const [users, setUsers] = useState<SettingsUser[]>([]);
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [permissions, setPermissions] = useState<Record<number, Record<string, string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'EMPLOYEE'>('EMPLOYEE');
  const [inviting, setInviting] = useState(false);
  const [planCode, setPlanCode] = useState<'A' | 'B' | 'C'>('A');
  const toast = useToast();
  const { canWrite } = useAuth();

  const load = useCallback(async () => {
    try {
      const [uRes, mRes, pRes, currentPlan] = await Promise.all([
        settingsApi.getUsers(),
        settingsApi.getModules(),
        settingsApi.getUserModulePermissions(),
        billingApi.getCurrentPlan(),
      ]);
      setUsers(uRes.users);
      setModules(mRes.modules.filter((m) => m.enabled));
      setPermissions(pRes.permissions);
      setPlanCode(currentPlan.planCode);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const getLevel = (userId: number, moduleKey: string) => {
    return permissions[userId]?.[moduleKey] ?? 'NONE';
  };

  const setLevel = (userId: number, moduleKey: string, level: string) => {
    setPermissions((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] ?? {}),
        [moduleKey]: level,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries: Array<{ userId: number; moduleKey: string; accessLevel: string }> = [];
      for (const u of users) {
        for (const m of modules) {
          entries.push({
            userId: u.id,
            moduleKey: m.moduleKey,
            accessLevel: getLevel(u.id, m.moduleKey),
          });
        }
      }
      await settingsApi.setUserModulePermissions(entries);
      toast.success('Behörigheter sparade');
      window.dispatchEvent(new CustomEvent('apisai:nav-modules-changed'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteOrCreate = async () => {
    setInviting(true);
    try {
      const result = await settingsApi.inviteOrCreateUser({
        email: inviteEmail.trim(),
        firstName: inviteFirstName.trim(),
        lastName: inviteLastName.trim(),
        role: inviteRole,
      });
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      toast.success(result.createdUser ? 'Användare skapad och inbjuden' : 'Användare kopplad till företaget');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte lägga till användare');
    } finally {
      setInviting(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      !filterUser ||
      u.displayName.toLowerCase().includes(filterUser.toLowerCase()) ||
      u.email.toLowerCase().includes(filterUser.toLowerCase())
  );

  if (loading) return <p className="text-sm text-gray-500">Laddar...</p>;

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Användarbehörigheter</h2>
      <p className="text-sm text-gray-600">
        Sätt åtkomstnivå per modul: NONE, READ, WRITE, ADMIN.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Filtrera användare"
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="w-48"
        />
        <WithWrite moduleCode="settings">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-2 py-1 text-sm bg-success text-white rounded hover:bg-success-hover disabled:opacity-50"
          >
            {saving ? 'Sparar...' : 'Spara'}
          </button>
        </WithWrite>
      </div>

      <div className="border border-gray-200 rounded p-3 bg-gray-50 space-y-2">
        <h3 className="text-sm font-semibold">Lägg till användare i företaget</h3>
        <p className="text-xs text-gray-600">Endast plan C kan bjuda in eller skapa användare.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            type="email"
            placeholder="E-post"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <input
            type="text"
            placeholder="Förnamn"
            value={inviteFirstName}
            onChange={(e) => setInviteFirstName(e.target.value)}
          />
          <input
            type="text"
            placeholder="Efternamn"
            value={inviteLastName}
            onChange={(e) => setInviteLastName(e.target.value)}
          />
          <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'ADMIN' | 'EMPLOYEE')}>
            <option value="EMPLOYEE">EMPLOYEE</option>
            <option value="ADMIN">ADMIN</option>
          </select>
        </div>
        <WithWrite moduleCode="settings">
          <button
            type="button"
            onClick={handleInviteOrCreate}
            disabled={inviting || planCode !== 'C' || inviteEmail.trim() === ''}
            className="px-2 py-1 text-sm bg-success text-white rounded hover:bg-success-hover disabled:opacity-50"
          >
            {inviting ? 'Skapar...' : 'Lägg till användare'}
          </button>
        </WithWrite>
      </div>

      <TableWrapper maxHeight="calc(100vh - 14rem)">
        <TableHead>
          <tr>
            <th className="text-left px-2 py-1.5 font-medium whitespace-nowrap">Användare</th>
            {modules.map((m) => (
              <th key={m.moduleKey} className="text-left px-2 py-1.5 font-medium whitespace-nowrap">
                {m.name}
              </th>
            ))}
          </tr>
          <FilterRow>
            <td className="px-2 py-1" />
            {modules.map((m) => (
              <td key={m.moduleKey} className="px-2 py-1" />
            ))}
          </FilterRow>
        </TableHead>
        <tbody>
          {filteredUsers.map((u) => (
            <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-2 py-1 whitespace-nowrap">{u.displayName}</td>
              {modules.map((m) => (
                <td key={m.moduleKey} className="px-2 py-1">
                  {canWrite('settings') ? (
                    <select
                      value={getLevel(u.id, m.moduleKey)}
                      onChange={(e) => setLevel(u.id, m.moduleKey, e.target.value)}
                      className="w-full max-w-[6rem] py-0.5 text-xs"
                    >
                      {ACCESS_LEVELS.map((l) => (
                        <option key={l} value={l}>
                          {l}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-gray-600">{getLevel(u.id, m.moduleKey)}</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </TableWrapper>
    </div>
  );
}
